const NEUTRAL_FACTOR = 1;

// An unanswered offer counts as half a refusal: silence can be a missed
// notification, but ignoring it outright would reward timing out over declining.
export const NO_RESPONSE_REFUSAL_WEIGHT = 0.5;

// Early completion: marking an order delivered farther than the threshold from
// the drop-off pin. Code constants, not SiteSettings; weight 0 disables it.
export const EARLY_COMPLETION_WEIGHT = 2;
export const EARLY_COMPLETION_THRESHOLD_METERS = 500;
export const EARLY_COMPLETION_MIN_COMPLETIONS = 5;

const clampRate = (rate) => Math.min(1, Math.max(0, rate));

/**
 * Share of offers answered yes, 0..1, with an unanswered offer credited as half.
 * null when there are no offers to judge.
 */
export function acceptanceRate(driverStats) {
    if (!driverStats || !(driverStats.offers > 0)) {
        return null;
    }

    const noResponse = Math.max(0, Number(driverStats.noResponse) || 0);
    const credited = driverStats.accepted + (1 - NO_RESPONSE_REFUSAL_WEIGHT) * noResponse;
    return clampRate(credited / driverStats.offers);
}

/**
 * Distance multiplier for a driver's acceptance record. A multiplier, not an
 * added distance, so it behaves the same where drivers sit 400 m apart and 8 km
 * apart. Too little history means no penalty.
 */
export function penaltyFactor(driverStats, { penaltyWeight, minOffers }) {
    if (!driverStats || driverStats.offers < minOffers) {
        return NEUTRAL_FACTOR;
    }

    const rate = acceptanceRate(driverStats);

    if (rate === null) {
        return NEUTRAL_FACTOR;
    }

    return NEUTRAL_FACTOR + penaltyWeight * (1 - rate);
}

/**
 * Share of located completions marked done away from the drop-off, 0..1.
 * null when there is nothing to judge.
 */
export function earlyCompletionRate(driverStats) {
    if (!driverStats || !(driverStats.completions > 0)) {
        return null;
    }

    return clampRate((Number(driverStats.earlyCompletions) || 0) / driverStats.completions);
}

// Distance multiplier for completing orders away from the drop-off.
export function earlyCompletionFactor(driverStats) {
    if (!driverStats || !(driverStats.completions >= EARLY_COMPLETION_MIN_COMPLETIONS)) {
        return NEUTRAL_FACTOR;
    }

    return NEUTRAL_FACTOR + EARLY_COMPLETION_WEIGHT * earlyCompletionRate(driverStats);
}

// Admin override in force, 1..100, or null. Number(null) is 0, which would
// read as the lowest priority.
export function overridePriorityPercent(driverStats) {
    const override = driverStats && driverStats.override;
    const percent = override && override.priorityPercent !== null && override.priorityPercent !== undefined
        ? Number(override.priorityPercent)
        : NaN;

    return Number.isFinite(percent) ? Math.min(100, Math.max(1, percent)) : null;
}

/**
 * The full multiplier dispatch applies to this driver's distance: acceptance
 * times early completion. An admin override replaces both with its priority
 * percent and ignores the minimums. driverPenaltyWeight 0 is the kill switch
 * and also silences overrides.
 */
export function priorityFactor(driverStats, config) {
    const override = overridePriorityPercent(driverStats);

    if (override !== null && config.penaltyWeight > 0) {
        return 100 / override;
    }

    return penaltyFactor(driverStats, config) * earlyCompletionFactor(driverStats);
}

// Admin-facing form of priorityFactor: 100 is normal, 50 ranks as if twice as far.
export function priorityPercent(factor) {
    return factor > 0 ? Math.round(100 / factor) : 100;
}

/**
 * Order candidates by priority, best first.
 *
 * The detour cap drops anyone farther than (nearest + maxDetourKm) on real
 * distance; only then does the record reorder who is left. So a poor record can
 * never send an order to a distant driver. The nearest candidate always survives
 * the cap, so this can never return an empty list for a non-empty input.
 *
 * @param {Array<{id: string, distance: number}>} candidates - already within the geofence
 * @param {Map<string, {offers: number, accepted: number, completions?: number, earlyCompletions?: number, override?: object}>} stats - keyed by driver id
 */
export function rankDriverCandidates(candidates, stats, config) {
    // Number(null) is 0, which would pass as a driver standing on the pickup point.
    const toDistance = (value) =>
        (value === null || value === undefined || value === '' ? NaN : Number(value));

    const withDistance = (candidates || [])
        .map(candidate => ({ ...candidate, distance: toDistance(candidate.distance) }))
        .filter(candidate => Number.isFinite(candidate.distance));

    if (withDistance.length === 0) {
        return [];
    }

    const nearest = Math.min(...withDistance.map(candidate => candidate.distance));
    const detourCap = nearest + config.maxDetourKm;

    return withDistance
        .filter(candidate => candidate.distance <= detourCap)
        .map(candidate => ({
            ...candidate,
            score: candidate.distance * priorityFactor(stats.get(String(candidate.id)), config)
        }))
        .sort((a, b) => a.score - b.score);
}
